import App from '../../../app';
import { ComplianceActionTypes } from '../../../app/package/base/service/activation-flow/activation-flow-domain';
import type { DepositNavigationOptions } from '../../../app/package/base/service/product/product-domain';
import type { TrackingEventSource } from '../../../app/package/base/service/tracking/tracking-service';
import { type GameDetails, type LobbyGame, TileType } from '../../base/package/api/package/gaming/gaming-domain';
import { I18n } from '../../base/package/services/package/i18n/i18n.de';
import type { I18nService } from '../../base/package/services/package/i18n/i18n-service';
import { dispatchCustomEvent } from '../../base/package/util/event';
import {
  BONUS_MODULE_EVENT,
  type BonusEventDetail,
  BonusModuleEventTypes,
  BonusPageView,
  type Campaign,
} from '../types';

export const EligibleGameVariation = {
  CUSTOM_ACTION: 'CUSTOM_ACTION',
  ONE_GAME: 'ONE_GAME',
  ONE_PROVIDER: 'ONE_PROVIDER',
  DIFFERENT_PROVIDERS: 'DIFFERENT_PROVIDERS',
  ALL_GAMES: 'ALL_GAMES',
} as const;

export type EligibleGameVariation = (typeof EligibleGameVariation)[keyof typeof EligibleGameVariation];

export function getPlayButton({
  campaign,
  element,
  isFreeSpin = false,
}: {
  campaign: Campaign;
  element: HTMLElement;
  isFreeSpin: boolean;
}) {
  const { providerCampaign, gamesIncluded } = campaign.externalConfig || {};

  if (isFreeSpin && providerCampaign?.games.length) {
    // if only one eligible game - navigate to the particular game
    // this feature is required only for free spin campaigns
    if (providerCampaign?.games.length === 1) {
      const gameId = `${providerCampaign.integration}:${providerCampaign.games[0]}`;
      return {
        variation: EligibleGameVariation.ONE_GAME,
        clickCb: () => App.instance.router.navigateToGame(true, gameId),
      };
    }

    // show the list of eligible games (if free spin campaign)
    return {
      variation: EligibleGameVariation.DIFFERENT_PROVIDERS,
      clickCb: () => navigateToGamesPage(campaign.id, element),
    };
  }

  if (!isFreeSpin && gamesIncluded?.length) {
    if (gamesIncluded?.length === 1) {
      const gameId = gamesIncluded[0];
      return {
        variation: EligibleGameVariation.ONE_GAME,
        clickCb: () => App.instance.router.navigateToGame(true, gameId!),
      };
    }

    const commonProvider = findCommonProviderForAllGames(gamesIncluded);
    // show the list of eligible games (if bonus money campaign)

    if (commonProvider) {
      return {
        provider: commonProvider,
        variation: EligibleGameVariation.ONE_PROVIDER,
        clickCb: () => navigateToGamesPage(campaign.id, element),
      };
    }

    return {
      variation: EligibleGameVariation.DIFFERENT_PROVIDERS,
      clickCb: () => navigateToGamesPage(campaign.id, element),
    };
  }

  // no particular game list specified, meaning all games are eligible
  return {
    variation: EligibleGameVariation.ALL_GAMES,
    clickCb: () => App.instance.router.navigateToHome(),
  };
}

export function getOptInButton(
  t: I18nService,
  campaign: Campaign,
  element: HTMLElement,
  source: 'optinInfoPg' | 'optinPg',
) {
  return {
    label: campaign.isEventBased ? t.get(I18n.mod.bonus.optIn) : t.get(I18n.mod.bonus.claimNow),
    clickCb: () =>
      subscribeToCampaign({
        campaign,
        element,
        source,
      }),
  };
}

export function getPostOptInButton(
  t: I18nService,
  campaign?: Campaign,
  tracking?: TrackingEventSource,
  depositOptions?: DepositNavigationOptions,
) {
  const { postOptInCTA, postOptInLink } = campaign?.content || {};
  if (postOptInCTA) {
    return {
      label: postOptInCTA,
      clickCb: postOptInLink
        ? () => App.instance.router.navigateTo(postOptInLink)
        : () => App.instance.router.navigateToHome(),
    };
  }

  const isOptInOnlyCampaign = campaign?.subscriptionEvent === 'crm:manual';
  if (isOptInOnlyCampaign) {
    return {
      label: t.get(I18n.base.playNow),
      clickCb: () => App.instance.router.navigateToHome(),
    };
  }

  return {
    label: t.get(I18n.base.deposit),
    clickCb: () => App.instance.product.gotoDeposit(false, tracking, campaign, depositOptions),
  };
}

export function getProvider(provider: string | undefined) {
  const lobby = App.instance.content.getLobby().value;

  if (lobby && provider) {
    return lobby.studios.find((studio) =>
      studio.name.toLowerCase().includes(provider === 'gt' ? 'greentube' : provider),
    );
  }

  return undefined;
}

export function getProviderById(provider: string | undefined) {
  const lobby = App.instance.content.getLobby().value;

  if (lobby && provider) {
    return lobby.studios.find((studio) => studio.id === provider);
  }

  return undefined;
}

export function navigateToGamesPage(id: string, element: HTMLElement) {
  const details = {
    type: BonusModuleEventTypes.SET_VIEW,
    payload: {
      view: BonusPageView.BONUS_GAMES,
      id,
    },
  };
  dispatchCustomEvent(element, BONUS_MODULE_EVENT, details);
}

export function subscribeToCampaign({
  campaign,
  element,
  source,
}: {
  campaign: Campaign;
  element: HTMLElement;
  source?: 'optinPg' | 'optinInfoPg';
}) {
  if (!App.instance.activationFlow.isActionEnabled(ComplianceActionTypes.INCENTIVE)) {
    App.instance.activationFlowModalService.requestUiUpdate();
    return;
  }

  window.$app.track.depositOffers({
    eventAction: 'BonusOffersOptIn',
    BonusID: campaign.id,
    source,
    bonusType: campaign.type,
  });

  const detail: BonusEventDetail = {
    type: campaign.restriction === 'WELCOME' ? BonusModuleEventTypes.CLAIM_WELCOME : BonusModuleEventTypes.SUBSCRIBE,
    payload: { campaign },
  };
  dispatchCustomEvent(element, BONUS_MODULE_EVENT, detail);
}

export async function getBonusMoneyCarouselGames(
  gamesIncluded: string[] | undefined,
): Promise<(GameDetails | LobbyGame)[]> {
  const games: GameDetails[] = [];
  if (!gamesIncluded?.length) {
    return await getBeliebteSpiele();
  }

  for (const gameId of gamesIncluded) {
    const game = await getGame(gameId);
    if (game) games.push(game);
  }

  return games;
}

export async function getBeliebteSpiele() {
  const beliebteSpieleSlug = window.$app.casinoConfig.beliebteSpieleSlug;
  const lobby = await App.instance.content.getLobby().getValue();
  return lobby?.categories.find((c: any) => c.filterId === beliebteSpieleSlug)?.games || [];
}

export async function getGame(gameId: string, integration?: string, tileType = TileType.P1) {
  const game = gameId.includes(':') ? gameId : `${integration}:${gameId}`;

  return await App.instance.content
    .getGameInfo(game, undefined, tileType)
    .catch((err) => window.$app.logger.warn(`Error fetching game info '${game}'`, err));
}

function findCommonProviderForAllGames(gamesIncluded: string[]) {
  let previousProviderId: string | undefined;
  let isOneProvider = true;

  for (const gameId of gamesIncluded) {
    const currentProviderId = gameId.split(':')[0];

    if (previousProviderId === undefined) {
      previousProviderId = currentProviderId;
    } else if (isOneProvider && previousProviderId !== currentProviderId) {
      isOneProvider = false;
    }
  }

  return isOneProvider ? getProvider(previousProviderId) : undefined;
}
